/**
 * SalesGoalsCard — ★ Fase 12.B: obiettivi mensili di vendita del team.
 *
 * Visibile a:
 *  - Direttrice Commerciale (user.crm_tenant_locked, es. Sofia · ES): può
 *    impostare/aggiornare gli obiettivi del proprio team.
 *  - Admin/amministrativo quando il tenant CRM attivo è 'es'.
 *
 * Mostra il progresso del mese corrente su: fatturato (€) e contratti firmati.
 * Gli stessi numeri compaiono nel report settimanale email.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Target, Pencil, Loader2, Trophy, FileSignature, Euro, History } from 'lucide-react';
import { useAuth } from '../../lib/auth-context';
import { useCrmTenant } from '../CrmTenantSwitcher';

const eur = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;

function GoalRow({ icon: Icon, label, doneLabel, targetLabel, pct, reachedLabel, testId }) {
  const val = pct == null ? 0 : Math.min(100, pct);
  const reached = pct != null && pct >= 100;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Icon className="w-3.5 h-3.5" /> {label}
        </span>
        <span className="font-medium tabular-nums">
          {doneLabel} / {targetLabel}
          <span className={`ml-1.5 ${reached ? 'text-emerald-600 font-semibold' : 'text-muted-foreground'}`}>
            {pct == null ? '—' : `${pct}%`}
          </span>
          {reached && (
            <span className="inline-flex items-center gap-0.5 ml-1.5 text-emerald-600 text-[10px] font-semibold">
              <Trophy className="w-3 h-3" /> {reachedLabel}
            </span>
          )}
        </span>
      </div>
      <Progress value={val} className={reached ? '[&>div]:bg-emerald-500' : ''} />
    </div>
  );
}

export function SalesGoalsCard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { tenant } = useCrmTenant();
  const isAdminLike = ['admin', 'amministrativo'].includes((user?.role || '').toLowerCase());
  const lockedTenant = user?.crm_tenant_locked || null;
  // Direttrice sempre; admin solo quando guarda il CRM ES
  const goalTenant = lockedTenant || (isAdminLike && tenant === 'es' ? 'es' : null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [revTarget, setRevTarget] = useState('');
  const [conTarget, setConTarget] = useState('');
  const [saving, setSaving] = useState(false);
  // ★ Fase 13 — storico obiettivi mesi passati
  const [histOpen, setHistOpen] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [histItems, setHistItems] = useState([]);

  const load = useCallback(() => {
    if (!goalTenant) return;
    setLoading(true);
    api.get('/sales-goals/current', { params: { language: goalTenant } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [goalTenant]);

  useEffect(() => { load(); }, [load]);

  if (!goalTenant) return null;

  const openEdit = () => {
    setRevTarget(data?.revenue_target ? String(data.revenue_target) : '');
    setConTarget(data?.contracts_target ? String(data.contracts_target) : '');
    setEditOpen(true);
  };

  // ★ Fase 13 — apre il dialog storico e carica i mesi passati
  const openHistory = () => {
    setHistOpen(true);
    setHistLoading(true);
    api.get('/sales-goals/history', { params: { language: goalTenant } })
      .then((r) => setHistItems(r.data?.items || []))
      .catch(() => setHistItems([]))
      .finally(() => setHistLoading(false));
  };

  const save = async () => {
    const rev = parseFloat(revTarget) || 0;
    const con = parseInt(conTarget, 10) || 0;
    if (rev <= 0 && con <= 0) {
      toast.error(t('goals.errAtLeastOne', 'Imposta almeno un obiettivo (fatturato o contratti).'));
      return;
    }
    setSaving(true);
    try {
      await api.put('/sales-goals/current', {
        revenue_target: rev, contracts_target: con, language: goalTenant,
      });
      toast.success(t('goals.saved', 'Obiettivi del mese aggiornati'));
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('goals.errSave', 'Errore salvataggio obiettivi'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border" data-testid="sales-goals-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Target className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
            <span className="text-sm font-medium truncate">
              {t('goals.title', 'Obiettivi del mese')}
              <span className="text-muted-foreground font-normal"> · {data?.month_label || ''}</span>
              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded px-1 py-0.5">
                {goalTenant.toUpperCase()}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={openHistory} data-testid="goals-history-btn">
              <History className="w-3.5 h-3.5 mr-1" />
              {t('goals.history', 'Storico')}
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={openEdit} data-testid="goals-edit-btn">
              <Pencil className="w-3.5 h-3.5 mr-1" />
              {t('goals.edit', 'Imposta obiettivi')}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2"><Skeleton className="h-6" /><Skeleton className="h-6" /></div>
        ) : !data ? (
          <p className="text-xs text-muted-foreground">{t('goals.errLoad', 'Impossibile caricare gli obiettivi.')}</p>
        ) : !data.has_goal ? (
          <p className="text-xs text-muted-foreground italic" data-testid="goals-empty">
            {t('goals.noGoal', 'Nessun obiettivo impostato per questo mese: usa «Imposta obiettivi» per definirlo.')}
          </p>
        ) : (
          <div className="space-y-3">
            {data.revenue_target > 0 && (
              <GoalRow
                icon={Euro}
                label={t('goals.revenue', 'Fatturato')}
                doneLabel={eur(data.progress?.revenue)}
                targetLabel={eur(data.revenue_target)}
                pct={data.progress?.revenue_pct}
                reachedLabel={t('goals.reached', 'Obiettivo raggiunto!')}
                testId="goals-revenue-row"
              />
            )}
            {data.contracts_target > 0 && (
              <GoalRow
                icon={FileSignature}
                label={t('goals.contracts', 'Contratti firmati')}
                doneLabel={String(data.progress?.contracts_signed ?? 0)}
                targetLabel={String(data.contracts_target)}
                pct={data.progress?.contracts_pct}
                reachedLabel={t('goals.reached', 'Obiettivo raggiunto!')}
                testId="goals-contracts-row"
              />
            )}
            {data.set_by_name && (
              <p className="text-[10px] text-muted-foreground">
                {t('goals.setBy', 'Impostato da')} {data.set_by_name}
              </p>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={editOpen} onOpenChange={(o) => !o && setEditOpen(false)}>
        <DialogContent className="max-w-md" data-testid="goals-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[hsl(var(--primary))]" />
              {t('goals.dialogTitle', 'Obiettivi del mese')} · {data?.month_label || ''}
            </DialogTitle>
            <DialogDescription>
              {t('goals.dialogDesc', 'Il progresso è visibile in dashboard e nel report settimanale del team.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">{t('goals.revenueTarget', 'Obiettivo fatturato (€)')}</Label>
              <Input
                type="number" min="0" step="500"
                value={revTarget}
                onChange={(e) => setRevTarget(e.target.value)}
                placeholder="es. 50000"
                className="mt-1"
                data-testid="goals-revenue-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">{t('goals.contractsTarget', 'Obiettivo contratti firmati')}</Label>
              <Input
                type="number" min="0" step="1"
                value={conTarget}
                onChange={(e) => setConTarget(e.target.value)}
                placeholder="es. 10"
                className="mt-1"
                data-testid="goals-contracts-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={saving}>
              {t('common.cancel', 'Annulla')}
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="goals-save-btn"
              className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
              {t('goals.save', 'Salva obiettivi')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ★ Fase 13 — Storico obiettivi: confronto raggiunto/target dei mesi */}
      <Dialog open={histOpen} onOpenChange={(o) => !o && setHistOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="goals-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[hsl(var(--primary))]" />
              {t('goals.historyTitle', 'Storico obiettivi')}
              <span className="text-xs uppercase tracking-wide text-muted-foreground border border-border rounded px-1 py-0.5">
                {goalTenant.toUpperCase()}
              </span>
            </DialogTitle>
            <DialogDescription>
              {t('goals.historyDesc', 'Andamento mese per mese: raggiunto rispetto al target.')}
            </DialogDescription>
          </DialogHeader>
          {histLoading ? (
            <div className="space-y-2"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
          ) : histItems.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-4" data-testid="goals-history-empty">
              {t('goals.noHistory', 'Nessuno storico disponibile: gli obiettivi appariranno qui mese dopo mese.')}
            </p>
          ) : (
            <div className="space-y-2.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="goals-history-list">
              {histItems.map((h) => (
                <div
                  key={`${h.year}-${h.month}`}
                  className="rounded-md border border-border bg-[var(--surface-2)]/50 p-3 space-y-2"
                  data-testid={`goals-history-item-${h.year}-${h.month}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{h.month_label}</span>
                    {h.is_current && (
                      <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 rounded px-1.5 py-0.5">
                        {t('goals.inProgress', 'In corso')}
                      </span>
                    )}
                  </div>
                  {h.revenue_target > 0 && (
                    <GoalRow
                      icon={Euro}
                      label={t('goals.revenue', 'Fatturato')}
                      doneLabel={eur(h.revenue_done)}
                      targetLabel={eur(h.revenue_target)}
                      pct={h.revenue_pct}
                      reachedLabel={t('goals.reached', 'Obiettivo raggiunto!')}
                      testId={`goals-history-revenue-${h.year}-${h.month}`}
                    />
                  )}
                  {h.contracts_target > 0 && (
                    <GoalRow
                      icon={FileSignature}
                      label={t('goals.contracts', 'Contratti firmati')}
                      doneLabel={String(h.contracts_done ?? 0)}
                      targetLabel={String(h.contracts_target)}
                      pct={h.contracts_pct}
                      reachedLabel={t('goals.reached', 'Obiettivo raggiunto!')}
                      testId={`goals-history-contracts-${h.year}-${h.month}`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
